import { Component, OnInit } from '@angular/core';

import { AuthorizationService, PermissionControlService, AclControlService } from '@mirage-projects/cw-commons';
import { PcvizCollageWebFileBrowserListResult } from '@mirage-projects/cw-models';
import { FilesHelperService } from '@mirage-projects/cw-utils';

import { FileBrowserOptions, PopupFileBrowserService } from './popup-file-browser.service';

type FileOptionsKeys = keyof NonNullable<FileBrowserOptions['fileOptions']>;

/**
 * This component displays the popup with the file browser
 */
@Component({
    selector: 'cw-popup-file-browser',
    templateUrl: './popup-file-browser.component.html',
    styleUrls: ['./popup-file-browser.component.scss'],
    standalone: false,
})
export class PopupFileBrowserComponent implements OnInit {
    titleKey = 'FILE_BROWSER.TITLE';

    constructor(
        public popupFileBrowserService: PopupFileBrowserService,
        private filesHelperService: FilesHelperService,
        private authorizationService: AuthorizationService,
        private permissionControlService: PermissionControlService,
        private aclControlService: AclControlService
    ) {}

    ngOnInit(): void {
        if (this.popupFileBrowserService.options?.title) {
            this.titleKey = this.popupFileBrowserService.options.title;
        }
    }

    /**
     * @param file
     * @returns true if the file size must be shown for the input file
     */
    showFileSize(file: PcvizCollageWebFileBrowserListResult): boolean {
        return this.shouldShowFileOption(file, 'showSize');
    }

    /**
     * @param file
     * @returns true if the file type must be shown for the input file
     */
    showFileType(file: PcvizCollageWebFileBrowserListResult): boolean {
        const isFile = this.popupFileBrowserService.isFile(file);
        const fileOptions = this.popupFileBrowserService.options?.fileOptions;

        return !!(fileOptions ? isFile && fileOptions.showType : false);
    }

    /**
     * @param file
     * @returns true if the delete button must be shown for the input file
     */
    showDeleteButton(file: PcvizCollageWebFileBrowserListResult): boolean {
        return this.shouldShowFileOption(file, 'showDelete');
    }

    /**
     * @param file
     * @returns true if the download button must be shown for the input file
     */
    showDownloadButton(file: PcvizCollageWebFileBrowserListResult): boolean {
        return (
            this.popupFileBrowserService.downloadingFile !== file &&
            (this.popupFileBrowserService.isFile(file) || !this.authorizationService.isCurrentUserAnonymous())
        );
    }

    /**
     * @returns true if the delete button must be disabled
     */
    isDeleteButtonDisabled(): boolean {
        const acl = this.popupFileBrowserService.catalogObjectType === 'layers' ? 'TYPE_LAYERS_UPDATE' : 'TYPE_PROJECTS_UPDATE';
        const hasAcl = this.aclControlService.hasUserACL(acl);
        const hasPermission = this.permissionControlService.canWriteByPermissions(this.popupFileBrowserService.catalogObject);
        const startsWithProccess = this.popupFileBrowserService.fileBrowser.getCurrentFolder().startsWith('process/');
        const isCurrentFolderProccess = this.popupFileBrowserService.fileBrowser.getCurrentFolder() === 'process';
        return startsWithProccess || isCurrentFolderProccess || !hasPermission || !hasAcl;
    }

    /**
     * @returns true if the go back button must be enabled
     */
    isEnabledGoBack(): boolean {
        return this.popupFileBrowserService.fileBrowserLevel > 0;
    }

    /**
     * @param file
     * @returns the translatable file type of the input file
     */
    getFileType(file: PcvizCollageWebFileBrowserListResult): string {
        const extension = `.${this.filesHelperService.getExtension(file.name)}`;
        switch (true) {
            case this.popupFileBrowserService.EXTENSIONS.TIN.includes(extension):
                return 'FILE_BROWSER.FILE_TYPE_TIN';
            case this.popupFileBrowserService.EXTENSIONS.REPORT.includes(extension):
                return 'FILE_BROWSER.FILE_TYPE_REPORT';
            default:
                return 'FILE_BROWSER.FILE_TYPE_SCAN';
        }
    }

    /**
     * @param file
     * @returns the icon of the input file
     */
    getIcon(file: PcvizCollageWebFileBrowserListResult): string {
        if (this.popupFileBrowserService.isDirectory(file)) {
            return 'folder';
        }

        const extension = this.filesHelperService.getExtension(file.name);
        switch (true) {
            case this.popupFileBrowserService.EXTENSIONS.IMAGE.includes(extension):
                return 'image';
            case this.popupFileBrowserService.EXTENSIONS.REPORT.includes(extension):
                return 'task';
            case this.popupFileBrowserService.EXTENSIONS.TIN.includes(extension):
                return '3d-model';
            default:
                return 'document';
        }
    }

    private shouldShowFileOption(file: PcvizCollageWebFileBrowserListResult, optionKey: FileOptionsKeys): boolean {
        const isFile = this.popupFileBrowserService.isFile(file);
        const fileOptions = this.popupFileBrowserService.options?.fileOptions;
        return !!(fileOptions ? isFile && fileOptions[optionKey] : isFile);
    }
}
